// battlemapsCommerce.js
// ONE authoritative source of Battlemaps commercial values for the whole portal.
//
// The obsolete "100 generations" figure survived a model change because the same
// numbers were written into several pages independently. Every Battlemaps
// surface now reads them from here, and here reads them from the backend
// endpoint that already owns them (GET /v1/battlemaps/commerce), so a price or
// credit quantity can only be changed in one place.
//
// The constants in battlemapsCatalog.js remain as the render-before-fetch
// default so a page never flashes a blank price; they are the same approved
// values the backend serves, and the fetched payload always wins.
import { useEffect, useState } from 'react';
import API from './api';
import {
  BATTLEMAPS_PACK_PRICE,
  BATTLEMAPS_PACK_INCLUDED_CREDITS,
  BATTLEMAPS_PACK_ENGINES,
  BATTLEMAPS_PACKS,
} from './battlemapsCatalog';

export const CREATION_CREDITS_LABEL = 'Creation Credits';

// Presentation order for the eight approved Creation Credit packs. Quantities
// and prices are NOT defined here - they come from the backend.
export const CREDIT_PACK_ORDER = [
  'starter', 'explorer', 'creator', 'worldbuilder',
  'cinematic', 'director', 'studio', 'production',
];

export const DEFAULT_COMMERCE = {
  currency: 'USD',
  environment_pack: {
    price_usd: BATTLEMAPS_PACK_PRICE,
    included_credits: BATTLEMAPS_PACK_INCLUDED_CREDITS,
    one_time: true,
    permanent: true,
    unlocks_engines: BATTLEMAPS_PACK_ENGINES,
  },
  credit_packs: [],
  credit_costs: {},
};

function normalise(payload) {
  if (!payload || typeof payload !== 'object') return DEFAULT_COMMERCE;
  const env = payload.environment_pack || {};
  const packs = Array.isArray(payload.credit_packs) ? payload.credit_packs : [];

  const byKey = {};
  packs.forEach((p) => { if (p && p.key) byKey[p.key] = p; });
  const ordered = CREDIT_PACK_ORDER.map((k) => byKey[k]).filter(Boolean);
  // Anything the server adds beyond the known eight still renders, after them.
  packs.forEach((p) => { if (p && CREDIT_PACK_ORDER.indexOf(p.key) === -1) ordered.push(p); });

  return {
    currency: payload.currency || DEFAULT_COMMERCE.currency,
    environment_pack: {
      price_usd: Number(env.price_usd != null ? env.price_usd : BATTLEMAPS_PACK_PRICE),
      included_credits: Number(env.included_credits != null
        ? env.included_credits : BATTLEMAPS_PACK_INCLUDED_CREDITS),
      one_time: env.one_time !== false,
      permanent: env.permanent !== false,
      unlocks_engines: Array.isArray(env.unlocks_engines) && env.unlocks_engines.length
        ? env.unlocks_engines : BATTLEMAPS_PACK_ENGINES,
    },
    credit_packs: ordered,
    credit_costs: payload.credit_costs || {},
  };
}

export function formatPrice(value, currency) {
  const n = Number(value || 0);
  const body = n % 1 === 0 ? String(n) : n.toFixed(2);
  return `$${body} ${currency || 'USD'}`;
}

/**
 * Battlemaps commercial configuration.
 *
 * Returns the approved defaults immediately and replaces them with the server's
 * authoritative payload once it arrives, so the page is never blank and never
 * shows a value the backend disagrees with.
 */
export function useBattlemapsCommerce() {
  const [commerce, setCommerce] = useState(DEFAULT_COMMERCE);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    API.get('/v1/battlemaps/commerce')
      .then((r) => { if (active) { setCommerce(normalise(r.data)); setLoaded(true); } })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  return { commerce, loaded };
}


// ---------------------------------------------------------------------------
// Environment Pack catalogue
// ---------------------------------------------------------------------------
// Same principle as the commercial values above, applied to WHICH packs exist.
// The catalogue used to be a hard-coded array in battlemapsCatalog.js, so
// publishing an Environment Pack meant editing React source and every surface
// that counted packs went stale until someone remembered. The two-BAT production
// pipeline now regenerates the catalogue and GET /v1/battlemaps/packs serves it,
// so a pack published by BAT 2 reaches every surface on its own.
//
// BATTLEMAPS_PACKS stays as the render-before-fetch default so the store, the
// pricing page and the dashboard are never blank while the request is in flight.

/** One shape whether the record came from the API or the fallback constant. */
export function normalisePackRecord(p) {
  const engines = Array.isArray(p.engines)
    ? p.engines.map((e) => (typeof e === 'string' ? e : (e.engine_name || e.engine_slug)))
    : (p.groups || []);
  return {
    id: p.pack_slug || p.id,
    name: p.pack_name || p.name,
    // `category` is what the surfaces group by. The API serves the authoritative
    // browse group from the master hierarchy; the constant carries the legacy key.
    category: p.group_slug || p.category_slug || p.category,
    categoryName: p.group_name || p.category_name || p.categoryName,
    generatorCount: p.generator_count != null ? p.generator_count : p.generatorCount,
    groups: engines,
    aliases: p.aliases || [],
    available: p.available !== false,
    price: p.price != null ? p.price : BATTLEMAPS_PACK_PRICE,
    includedCredits: p.includedCredits != null
      ? p.includedCredits : BATTLEMAPS_PACK_INCLUDED_CREDITS,
  };
}

const FALLBACK_PACKS = BATTLEMAPS_PACKS.map(normalisePackRecord);

/**
 * The published Environment Pack catalogue.
 *
 * Returns the fallback catalogue immediately and replaces it with the published
 * one once it arrives. A failed request leaves the fallback in place rather than
 * emptying the store.
 */
export function useBattlemapsPacks() {
  const [packs, setPacks] = useState(FALLBACK_PACKS);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    API.get('/v1/battlemaps/packs', { params: { per_page: 400 } })
      .then((r) => {
        if (!active) return;
        const list = (r.data && r.data.packs) || [];
        if (list.length) setPacks(list.map(normalisePackRecord));
        setLoaded(true);
      })
      .catch(() => { if (active) setLoaded(true); });
    return () => { active = false; };
  }, []);

  const byId = {};
  packs.forEach((p) => { byId[p.id] = p; });
  return { packs, packsById: byId, loaded };
}

/**
 * Battlemaps account state: owned Environment Packs and Creation Credit balance.
 */
export function useBattlemapsAccount() {
  const [account, setAccount] = useState({
    ownedSlugs: new Set(), ownedPacks: [], creationCredits: 0, loaded: false,
  });

  useEffect(() => {
    let active = true;
    API.get('/v1/battlemaps/account')
      .then((r) => {
        if (!active) return;
        const bm = (r.data && r.data.battlemaps) || r.data || {};
        const packs = bm.owned_packs || [];
        const credits = bm.creation_credits != null
          ? bm.creation_credits
          : (bm.generations != null ? bm.generations : 0);
        setAccount({
          ownedSlugs: new Set(packs.map((p) => p.pack_slug || p.slug || p)),
          ownedPacks: packs,
          creationCredits: Number(credits) || 0,
          loaded: true,
        });
      })
      .catch(() => { if (active) setAccount((a) => ({ ...a, loaded: true })); });
    return () => { active = false; };
  }, []);

  return account;
}
